import * as React from 'react';
import { FunctionComponent } from 'react';
import classNames from 'classnames';
import './SimpleModal.scss';

let openSimpleModalCount = 0;
let bodyOverflowBeforeModals = '';
const SimpleModalRequestCloseContext = React.createContext<(() => void) | undefined>(undefined);

export const useSimpleModalRequestClose = () => React.useContext(SimpleModalRequestCloseContext);

interface Props {
  isOpen: boolean;
  title?: string | JSX.Element;
  body?: string | JSX.Element;
  buttonText?: string;
  buttonDisabled?: boolean;
  buttonCancelText?: string;
  onButtonClick?: () => void;
  onCloseClick?: () => void;
  customClass?: string;
  onCloseIconClick?: () => void;
  showFooter?: boolean;
  dialogStyle?: React.CSSProperties;
  hideCancelButton?: boolean;
  isBodyBold?: boolean;
  bodyClassName?: string;
  showHeader?: boolean;
  isBare?: boolean;
  fullScreen?: boolean;
  onBackdropClick?: () => void;
  children?: React.ReactNode;
  zIndex?: number;
  side?: 'right';
  guardUnsavedChanges?: boolean;
  unsavedChangesMessage?: string;
}

export const SimpleModal: FunctionComponent<Props> = props => {
  const {
    isOpen,
    title,
    onCloseClick,
    body,
    children,
    buttonText,
    buttonDisabled = false,
    onButtonClick,
    buttonCancelText,
    customClass,
    onCloseIconClick,
    showFooter = true,
    dialogStyle,
    hideCancelButton = false,
    isBodyBold = true,
    bodyClassName,
    showHeader = true,
    isBare = false,
    fullScreen = false,
    onBackdropClick: onBackdropClickProp,
    zIndex = 1050,
    side,
    guardUnsavedChanges = false,
    unsavedChangesMessage = 'Your changes have not been saved. If you leave now, they will be lost.'
  } = props;
  const [hasUserChanges, setHasUserChanges] = React.useState(false);
  const [isDiscardWarningOpen, setIsDiscardWarningOpen] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setHasUserChanges(false);
      setIsDiscardWarningOpen(false);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;

    if (openSimpleModalCount === 0) {
      bodyOverflowBeforeModals = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    openSimpleModalCount += 1;

    return () => {
      openSimpleModalCount = Math.max(0, openSimpleModalCount - 1);
      if (openSimpleModalCount === 0) {
        document.body.style.overflow = bodyOverflowBeforeModals;
      }
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const modalClasses = classNames({
    mDisplayBlock: isOpen,
    show: isOpen,
    modal: true,
    fade: true
  });

  const isTitleExist = typeof title !== 'undefined';
  const isOnCloseClickFunction = typeof onCloseClick === 'function';
  const isOnCloseIconClickFunction = typeof onCloseIconClick === 'function';
  const isCloseIconRender = isOnCloseIconClickFunction || isOnCloseClickFunction;
  const isHeaderRender = showHeader && (isTitleExist || isCloseIconRender);
  const isBodyExist = typeof body !== 'undefined';
  const isChildrenElementsExist = typeof children !== 'undefined';
  const isBodyRender = isBodyExist || isChildrenElementsExist;
  const isButtonTextExist = typeof buttonText !== 'undefined';
  const isCancelRender = isOnCloseClickFunction && !hideCancelButton;
  const isOnButtonClickFunction = typeof onButtonClick === 'function';
  const isButtonRender = isButtonTextExist && isOnButtonClickFunction;
  const isFooterRender = showFooter && (isButtonRender || isCancelRender);

  const closeNow = () => {
    setHasUserChanges(false);
    setIsDiscardWarningOpen(false);
    if (isOnCloseIconClickFunction) onCloseIconClick();
    else if (isOnCloseClickFunction) onCloseClick();
  };

  const requestClose = () => {
    if (guardUnsavedChanges && hasUserChanges) setIsDiscardWarningOpen(true);
    else closeNow();
  };

  const dialogClasses = classNames('modal-dialog modal-dialog-scrollable modal-xl', customClass, {
    'modal-fullscreen': fullScreen
  });

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      if (typeof onBackdropClickProp === 'function') {
        onBackdropClickProp();
      } else requestClose();
    }
  };

  const bodyWrapClasses = classNames({ 'font-weight-bold': isBodyBold }, bodyClassName);

  if (isBare) {
    return (
      <div
        className={modalClasses}
        onClick={onBackdropClick}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24
        }}
      >
        <div
          style={{
            position: 'relative',
            height: fullScreen ? '100vh' : undefined,
            maxHeight: fullScreen ? '100vh' : 'calc(100vh - 48px)',
            overflow: 'auto'
          }}
          onClick={e => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      className={modalClasses}
      onClick={onBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.42)',
        backdropFilter: 'blur(3px)',
        zIndex,
        display: 'flex',
        alignItems: fullScreen || side === 'right' ? 'stretch' : 'center',
        justifyContent: side === 'right' ? 'flex-end' : 'center',
        padding: fullScreen ? 0 : side === 'right' ? 0 : 24
      }}
    >
      <div
        className={dialogClasses}
        style={{
          ...(dialogStyle || {}),
          width: fullScreen ? '100vw' : side === 'right' ? 'min(520px, 100vw)' : 'calc(100vw - 48px)',
          maxWidth: fullScreen ? 'none' : side === 'right' ? '520px' : dialogStyle?.maxWidth || 1100,
          height: fullScreen || side === 'right' ? '100vh' : dialogStyle?.height,
          margin: 0
        }}
      >
        <div
          className="modal-content"
          onInputCapture={() => guardUnsavedChanges && setHasUserChanges(true)}
          onChangeCapture={() => guardUnsavedChanges && setHasUserChanges(true)}
          style={{
            position: 'relative',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: fullScreen || side === 'right' ? 0 : 16,
            overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(15, 23, 42, 0.22)',
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
            height: fullScreen || side === 'right' ? '100vh' : undefined,
            maxHeight: fullScreen || side === 'right' ? '100vh' : 'calc(100vh - 48px)'
          }}
        >
          {!showHeader && isCloseIconRender && (
            <button
              type="button"
              onClick={requestClose}
              aria-label="Close"
              style={{
                position: 'absolute',
                right: 14,
                top: 14,
                zIndex: 2,
                width: 36,
                height: 36,
                borderRadius: 10,
                border: '1px solid rgba(0,0,0,0.08)',
                background: 'rgba(0,0,0,0.02)',
                color: 'rgba(0,0,0,0.55)',
                fontSize: 24,
                lineHeight: '32px',
                padding: 0,
                cursor: 'pointer'
              }}
            >
              <span>&times;</span>
            </button>
          )}

          {isHeaderRender && (
            <div
              className="modal-header"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '18px 20px',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                background: 'linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0))'
              }}
            >
              <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                {isTitleExist ? (
                  <h5
                    className="modal-title"
                    style={{
                      margin: 0,
                      fontSize: 18,
                      lineHeight: 1.25,
                      fontWeight: 700,
                      color: 'rgba(0,0,0,0.84)'
                    }}
                  >
                    {title}
                  </h5>
                ) : null}
              </div>

              {isCloseIconRender && (
                <button
                  type="button"
                  onClick={requestClose}
                  aria-label="Close"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    border: '1px solid rgba(0,0,0,0.08)',
                    background: 'rgba(0,0,0,0.02)',
                    color: 'rgba(0,0,0,0.55)',
                    fontSize: 24,
                    lineHeight: '32px',
                    padding: 0,
                    cursor: 'pointer',
                    flex: '0 0 auto'
                  }}
                >
                  <span>&times;</span>
                </button>
              )}
            </div>
          )}

          {isBodyRender && (
            <div
              className={classNames('modal-body mPreWrap', bodyClassName)}
              style={{
                padding: '22px 24px',
                background: '#fff',
                color: 'rgba(0,0,0,0.78)',
                fontSize: 14,
                lineHeight: 1.5,
                flex: '1 1 auto',
                minHeight: 0,
                overflow: 'auto'
              }}
            >
              {isBodyExist && <div className={bodyWrapClasses}>{body}</div>}
              {isChildrenElementsExist && (
                <SimpleModalRequestCloseContext.Provider value={requestClose}>
                  {children}
                </SimpleModalRequestCloseContext.Provider>
              )}
            </div>
          )}

          {isFooterRender && (
            <div
              className="modal-footer"
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 10,
                padding: '16px 24px',
                borderTop: '1px solid rgba(0,0,0,0.06)',
                background: 'rgba(0,0,0,0.015)'
              }}
            >
              {isCancelRender && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onCloseClick}
                  style={{
                    borderRadius: 10,
                    minHeight: 38,
                    paddingLeft: 14,
                    paddingRight: 14
                  }}
                >
                  {buttonCancelText}
                </button>
              )}

              {isButtonRender && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onButtonClick}
                  disabled={buttonDisabled}
                  style={{
                    borderRadius: 10,
                    minHeight: 38,
                    paddingLeft: 14,
                    paddingRight: 14,
                    ...(buttonDisabled
                      ? {
                          borderColor: 'var(--border-subtle, #e2e8f0)',
                          background: 'var(--surface-sunken, #f1f5f9)',
                          color: 'var(--text-subtle, #94a3b8)',
                          cursor: 'not-allowed',
                          opacity: 1
                        }
                      : {})
                  }}
                >
                  {buttonText}
                </button>
              )}
            </div>
          )}

          {isDiscardWarningOpen && (
            <div
              className="simpleModalUnsaved"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="simple-modal-unsaved-title"
            >
              <div className="simpleModalUnsaved__card">
                <div className="simpleModalUnsaved__notice">
                  <span aria-hidden="true">!</span>
                  <div>
                    <strong id="simple-modal-unsaved-title">Discard unsaved changes?</strong>
                    <p>{unsavedChangesMessage}</p>
                  </div>
                </div>
                <div className="simpleModalUnsaved__actions">
                  <button type="button" onClick={() => setIsDiscardWarningOpen(false)}>
                    Keep editing
                  </button>
                  <button type="button" className="is-danger" onClick={closeNow}>
                    Discard changes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

SimpleModal.defaultProps = {
  buttonCancelText: 'Cancel',
  hideCancelButton: false,
  isBodyBold: true,
  showHeader: true,
  isBare: false,
  fullScreen: false
};
